<template>
  <section class="py-24 lg:py-32 bg-[#fafbfc] relative overflow-hidden" id="kids-section">
    <div class="absolute top-0 right-0 w-[600px] h-[600px] bg-sky-100/50 blur-[120px] rounded-full pointer-events-none"></div>

    <div class="max-w-7xl mx-auto px-6 relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center">
        <div v-reveal class="flex flex-col items-start order-2 lg:order-1">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 border border-sky-200 mb-8 shadow-sm">
            <iconify-icon icon="solar:star-fall-minimalistic-2-bold" class="text-sky-500 text-lg"></iconify-icon>
            <span class="text-xs font-semibold text-sky-700 tracking-widest uppercase">
              {{ t('home.kids.badge') }}
            </span>
          </div>

          <h2 class="text-4xl lg:text-6xl font-serif text-[#1c4848] font-semibold mb-6 tracking-tight leading-tight">
            {{ t('home.kids.title') }}
          </h2>
          <p class="text-lg text-[#1c4848]/60 font-medium mb-12 max-w-lg leading-relaxed">
            {{ t('home.kids.subtitle') }}
          </p>

          <div class="space-y-6 w-full">
            <div class="flex items-start gap-5 p-4 rounded-3xl hover:bg-white transition-colors duration-300 border border-transparent hover:border-[#1c4848]/5 hover:shadow-sm">
              <div class="w-12 h-12 rounded-[18px] bg-rose-100 text-rose-500 flex items-center justify-center text-2xl shrink-0">
                <iconify-icon icon="solar:book-bookmark-linear"></iconify-icon>
              </div>
              <div>
                <h4 class="text-[#1c4848] text-lg font-semibold mb-1">
                  {{ t('home.kids.items.pictureBooks.title') }}
                </h4>
                <p class="text-sm text-[#1c4848]/60 font-medium">
                  {{ t('home.kids.items.pictureBooks.body') }}
                </p>
              </div>
            </div>

            <div class="flex items-start gap-5 p-4 rounded-3xl hover:bg-white transition-colors duration-300 border border-transparent hover:border-[#1c4848]/5 hover:shadow-sm">
              <div class="w-12 h-12 rounded-[18px] bg-indigo-100 text-indigo-500 flex items-center justify-center text-2xl shrink-0">
                <iconify-icon icon="solar:moon-stars-linear"></iconify-icon>
              </div>
              <div>
                <h4 class="text-[#1c4848] text-lg font-semibold mb-1">
                  {{ t('home.kids.items.lullabies.title') }}
                </h4>
                <p class="text-sm text-[#1c4848]/60 font-medium">
                  {{ t('home.kids.items.lullabies.body') }}
                </p>
              </div>
            </div>

            <div class="flex items-start gap-5 p-4 rounded-3xl hover:bg-white transition-colors duration-300 border border-transparent hover:border-[#1c4848]/5 hover:shadow-sm">
              <div class="w-12 h-12 rounded-[18px] bg-sky-100 text-sky-500 flex items-center justify-center text-2xl shrink-0">
                <iconify-icon icon="solar:soundwave-linear"></iconify-icon>
              </div>
              <div>
                <h4 class="text-[#1c4848] text-lg font-semibold mb-1">
                  {{ t('home.kids.items.whiteNoise.title') }}
                </h4>
                <p class="text-sm text-[#1c4848]/60 font-medium">
                  {{ t('home.kids.items.whiteNoise.body') }}
                </p>
              </div>
            </div>

            <div class="flex items-start gap-5 p-4 rounded-3xl hover:bg-white transition-colors duration-300 border border-transparent hover:border-[#1c4848]/5 hover:shadow-sm">
              <div class="w-12 h-12 rounded-[18px] bg-emerald-100 text-emerald-500 flex items-center justify-center text-2xl shrink-0">
                <iconify-icon icon="solar:gamepad-linear"></iconify-icon>
              </div>
              <div>
                <h4 class="text-[#1c4848] text-lg font-semibold mb-1">
                  {{ t('home.kids.items.games.title') }}
                </h4>
                <p class="text-sm text-[#1c4848]/60 font-medium">
                  {{ t('home.kids.items.games.body') }}
                </p>
              </div>
            </div>

            <div class="flex items-start gap-5 p-4 rounded-3xl bg-white border border-[#1c4848]/5 shadow-sm">
              <div class="w-12 h-12 rounded-[18px] bg-amber-100 text-amber-500 flex items-center justify-center text-2xl shrink-0 relative overflow-hidden">
                <div class="absolute inset-0 border-2 border-amber-400 rounded-[18px] scale-110 opacity-20"></div>
                <iconify-icon icon="solar:lock-password-linear"></iconify-icon>
              </div>
              <div>
                <h4 class="text-[#1c4848] text-lg font-semibold mb-1 flex items-center gap-2">
                  {{ t('home.kids.items.pin.title') }}
                  <span class="bg-amber-100 text-amber-600 text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider">
                    {{ t('home.kids.items.pin.badge') }}
                  </span>
                </h4>
                <p class="text-sm text-[#1c4848]/60 font-medium">
                  {{ t('home.kids.items.pin.body') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-reveal="{ delayMs: 150 }" class="relative w-full h-[500px] sm:h-[650px] flex items-center justify-center order-1 lg:order-2">
          <img
            src="https://fra1.digitaloceanspaces.com/aplusspace/118/K4hQ7IDZmPMVNN04CgX6aU3RJU0E46Yr6KWDoUUx.png"
            alt="Deenly Kids App Screen 2"
            class="absolute left-0 sm:left-10 top-0 w-[60%] sm:w-[300px] rounded-[32px] sm:rounded-[40px] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.2)] border-[6px] sm:border-[8px] border-white object-cover z-10 animate-float-delayed bg-white"
          />

          <img
            src="https://fra1.digitaloceanspaces.com/aplusspace/117/9Edo9a7GvIEo4uqJJdpazE4Vg77JjKtcjwZcN68q.png"
            alt="Deenly Kids App Screen 1"
            class="absolute right-0 sm:right-4 bottom-0 w-[65%] sm:w-[320px] rounded-[32px] sm:rounded-[40px] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] border-[6px] sm:border-[8px] border-white object-cover z-20 animate-float bg-white"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
</script>

